/* eslint-disable camelcase */
import { useState } from 'react';
import useFieldState from './useFieldState';
import vals from '../utils/validators';
import nors from '../utils/normalizers';
import useRecurlyFieldState, {
  creditCardErrorHandler,
  cvvErrorHandler,
  monthErrorHandler,
  yearErrorHandler,
} from '../Components/Recurly/hooks/useRecurlyFieldState';
import { notifyErrors } from '../helpers';
import {
  IErrorDetails,
  IFieldState,
  IRecurlyFieldState,
  IPayFieldsState,
  TokenPayload,
  RecurlyError,
} from '../../interfaces';

const usePayFieldsState = (hasCountry = false): IPayFieldsState => {
  const [lastToken, setToken] = useState<TokenPayload | string>('');
  const name: IFieldState = useFieldState(
    [(value: string) => vals.requiredText(value, 'name')],
    nors.names
  );
  const lastName: IFieldState = useFieldState(
    [(value: string) => vals.requiredText(value, 'last name')],
    nors.names
  );
  const number: IRecurlyFieldState = useRecurlyFieldState(creditCardErrorHandler);
  const month: IRecurlyFieldState = useRecurlyFieldState(monthErrorHandler);
  const year: IRecurlyFieldState = useRecurlyFieldState(yearErrorHandler);
  const cvv: IRecurlyFieldState = useRecurlyFieldState(cvvErrorHandler);
  const postalCode: IFieldState = useFieldState(
    [(value: string) => vals.requiredText(value, 'postal code')],
    nors.postCodeSoft
  );
  const country: IFieldState = useFieldState(
    hasCountry ? [(value: string) => vals.requiredText(value, 'country')] : []
  );
  const fields = {
    first_name: name,
    last_name: lastName,
    number,
    month,
    year,
    cvv,
    postal_code: postalCode,
    country,
  };

  function isIErrorDetails(err: IErrorDetails | RecurlyError | null): err is IErrorDetails {
    const length = err?.details?.length;
    return length !== undefined && length > 0;
  }

  const onResponse = (err: IErrorDetails | RecurlyError | null, token: TokenPayload) => {
    if (err && isIErrorDetails(err)) {
      notifyErrors({ err, fields });
    } else {
      setToken(token);
    }
  };

  const notHasError =
    name.isTouched &&
    lastName.isTouched &&
    number.isTouched &&
    month.isTouched &&
    year.isTouched &&
    cvv.isTouched &&
    postalCode.isTouched &&
    !name.hasError &&
    !lastName.hasError &&
    !number.hasError &&
    !month.hasError &&
    !year.hasError &&
    !cvv.hasError &&
    !postalCode.hasError &&
    (!hasCountry || (hasCountry && country.isTouched && !country.hasError));

  return {
    fields,
    hasErrors: !notHasError,
    lastToken,
    onResponse,
  };
};

export default usePayFieldsState;
